import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { AuthState, LoginCredentials, SignupData, SetPasswordData } from '../types/auth';
import { authApi } from '../services/api/authApi';
import { setUser as setSentryUser, Sentry, logger, metrics } from '../config/sentry';

interface AuthStore extends AuthState {
  // 2FA state
  requires2FA: boolean;
  pending2FAUserId: number | null;
  pending2FAEmail: string | null;

  // Actions
  login: (credentials: LoginCredentials) => Promise<boolean | '2fa_required'>;
  signup: (signupData: SignupData) => Promise<boolean>;
  resetPassword: (setPasswordData: SetPasswordData) => Promise<boolean>;
  logout: () => void;
  clearError: () => void;
  setLoading: (loading: boolean) => void;
  refreshToken: () => Promise<boolean>;
  setAuth: (user: any, token: string) => void;
  clear2FAState: () => void;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      requires2FA: false,
      pending2FAUserId: null,
      pending2FAEmail: null,

      // Actions
      login: async (credentials: LoginCredentials): Promise<boolean | '2fa_required'> => {
        set({ isLoading: true, error: null });

        try {
          const response: any = await authApi.login(credentials);

          // Check if 2FA is required
          if (response.data?.requires2FA) {
            set({
              requires2FA: true,
              pending2FAUserId: response.data.userId,
              pending2FAEmail: response.data.email,
              isLoading: false,
              error: null
            });
            return '2fa_required';
          }

          // Backend returns {success: true, data: {user, token}, message}
          const { user, token } = response.data;

          set({
            user: user,
            token: token,
            isAuthenticated: true,
            isLoading: false,
            error: null,
            requires2FA: false,
            pending2FAUserId: null,
            pending2FAEmail: null
          });

          // Store token in localStorage for API calls
          localStorage.setItem('authToken', token);

          // Set Sentry user context for error tracking
          setSentryUser({ id: String(user.id), email: user.email });
          logger.info('User logged in', { userId: String(user.id), email: user.email });
          metrics.increment('auth.login.success');

          return true;
        } catch (error: any) {
          set({
            isLoading: false,
            error: error.response?.data?.error || error.response?.data?.message || 'Login Failed'
          });
          metrics.increment('auth.login.failure');
          return false;
        }
      },

      signup: async (signupData: SignupData): Promise<boolean> => {
        set({ isLoading: true, error: null });
        
        try {
          const response: any = await authApi.signup(signupData);
          
          // Backend returns {success: true, data: {user, token}, message}
          const { user, token } = response.data;
          
          set({
            user: user,
            token: token,
            isAuthenticated: true,
            isLoading: false,
            error: null
          });
          
          // Store token in localStorage for API calls
          localStorage.setItem('authToken', token);
          
          return true;
        } catch (error: any) {
          set({
            isLoading: false,
            error: error.response?.data?.message || 'Signup failed'
          });
          return false;
        }
      },

      logout: () => {
        logger.info('User logged out');
        metrics.increment('auth.logout');
        localStorage.removeItem('authToken');
        Sentry.setUser(null); // Clear Sentry user context
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
          error: null
        });
      },

      resetPassword: async (setPasswordData: SetPasswordData): Promise<boolean> => {
        set({ isLoading: true, error: null });
        
        try {
          const response: any = await authApi.resetPassword(setPasswordData.token, setPasswordData.password);
          
          console.log(response.data?.message);

          set({
            isLoading: false,
            error: null
          });
          
          return true;
        } catch (error: any) {
          set({
            isLoading: false,
            error: error.response?.data?.error || 'Signup failed'
          });
          return false;
        }
      },

      clearError: () => {
        set({ error: null });
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      refreshToken: async (): Promise<boolean> => {
        const { token } = get();
        if (!token) return false;

        try {
          const response = await authApi.refreshToken();
          set({
            user: response.user,
            token: response.token,
            isAuthenticated: true
          });
          
          localStorage.setItem('authToken', response.token);
          return true;
        } catch (error) {
          console.error('Token refresh failed:', error);
          // Token refresh failed, logout user
          get().logout();
          return false;
        }
      },

      setAuth: (user: any, token: string) => {
        set({
          user: user,
          token: token,
          isAuthenticated: true,
          isLoading: false,
          error: null,
          requires2FA: false,
          pending2FAUserId: null,
          pending2FAEmail: null
        });

        // Store token in localStorage for API calls
        localStorage.setItem('authToken', token);

        // Set Sentry user context for error tracking
        setSentryUser({ id: String(user.id), email: user.email });
      },

      clear2FAState: () => {
        set({
          requires2FA: false,
          pending2FAUserId: null,
          pending2FAEmail: null
        });
      }
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated
      })
    }
  )
);